import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import {
  getServiceRequestByToken,
  createServiceCheckout,
  confirmServicePayment,
  submitServiceReview,
} from "@/lib/services.functions";
import { getStripeEnvironment } from "@/lib/stripe";
import { MarketingLayout as Layout, useHydratedTranslation } from "@/components/marketing/Layout";
import { buildSeo } from "@/lib/seo";
import { Check, CreditCard, Loader2, ShieldCheck, Star } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/services/pay/$token")({
  validateSearch: (s: Record<string, unknown>): { session_id?: string } => ({
    session_id: typeof s.session_id === "string" ? s.session_id : undefined,
  }),
  head: () =>
    buildSeo({
      path: "/services/pay",
      title: "Your quote — Reality Twin Services",
      description: "Review your quote and pay securely online.",
      noindex: true,
    }),
  component: PayPage,
});

function PayPage() {
  const t = useHydratedTranslation();
  const { token } = Route.useParams();
  const { session_id } = Route.useSearch();
  const fetchRequest = useServerFn(getServiceRequestByToken);
  const checkout = useServerFn(createServiceCheckout);
  const confirm = useServerFn(confirmServicePayment);

  const [request, setRequest] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [paying, setPaying] = useState(false);
  const [confirming, setConfirming] = useState(!!session_id);
  const review = useServerFn(submitServiceReview);
  const [stars, setStars] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [reviewBusy, setReviewBusy] = useState(false);
  const [reviewDone, setReviewDone] = useState(false);

  async function sendReview() {
    if (!stars) return;
    setReviewBusy(true);
    try {
      await review({ data: { token, rating: stars, comment: reviewText.trim() || undefined } });
      setReviewDone(true);
      toast.success(t("routes.pay.reviewThanks"));
    } catch (e: any) {
      toast.error(e?.message ?? t("routes.pay.reviewError"));
    } finally {
      setReviewBusy(false);
    }
  }

  async function load() {
    try {
      const r = await fetchRequest({ data: { token } });
      setRequest(r.request);
    } catch (e: any) {
      setError(e?.message ?? t("routes.pay.quoteNotFound"));
    }
  }

  useEffect(() => {
    (async () => {
      // Returning from Stripe: confirm first, then load the fresh status.
      if (session_id) {
        try {
          await confirm({
            data: { token, sessionId: session_id, environment: getStripeEnvironment() },
          });
        } catch (e: any) {
          toast.error(e?.message ?? t("routes.pay.confirmError"));
        } finally {
          setConfirming(false);
        }
      }
      await load();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, session_id]);

  async function pay() {
    setPaying(true);
    try {
      const r = await checkout({ data: { token, environment: getStripeEnvironment() } });
      window.location.href = r.url;
    } catch (e: any) {
      toast.error(e?.message ?? t("routes.pay.payStartError"));
      setPaying(false);
    }
  }

  if (error)
    return (
      <Layout>
        <div className="p-24 text-center text-muted-foreground">
          {error}{" "}
          <Link to="/services" className="text-[oklch(0.82_0.18_168)] hover:underline">
            {t("routes.pay.browseProviders")}
          </Link>
        </div>
      </Layout>
    );
  if (!request || confirming)
    return (
      <Layout>
        <div className="p-24 flex flex-col items-center gap-3 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin text-[oklch(0.82_0.18_168)]" />
          {confirming ? t("routes.pay.confirming") : t("routes.pay.loading")}
        </div>
      </Layout>
    );

  const paid = request.status === "paid" || request.status === "completed";

  return (
    <Layout>
      <section className="mx-auto max-w-lg px-6 py-24">
        <div className="glass-strong rounded-2xl p-8">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl brand-gradient overflow-hidden flex items-center justify-center text-black font-bold shrink-0">
              {request.provider_logo ? (
                <img src={request.provider_logo} alt="" className="h-full w-full object-cover" />
              ) : (
                request.provider_name[0]
              )}
            </div>
            <div>
              <div className="font-display text-lg font-semibold">{request.provider_name}</div>
              <div className="text-xs text-muted-foreground">
                {request.category
                  ? t(`routes.directory.categories.${request.category}`, {
                      defaultValue: request.category,
                    })
                  : t("routes.pay.service")}
                {request.provider_country ? ` · ${request.provider_country}` : ""}
              </div>
            </div>
          </div>

          <div className="mt-6 rounded-xl glass border border-border p-5">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono">
              {t("routes.pay.yourQuote")}
            </div>
            <div className="mt-2 font-display text-4xl font-semibold">
              {Number(request.quoted_amount).toLocaleString()}{" "}
              <span className="text-xl">{request.currency}</span>
            </div>
            {request.quoted_note && (
              <p className="mt-3 text-sm text-muted-foreground whitespace-pre-wrap">
                {request.quoted_note}
              </p>
            )}
            {request.preferred_date && (
              <p className="mt-2 text-xs text-muted-foreground">
                {t("routes.pay.preferredDate")}
                {request.preferred_date}
              </p>
            )}
          </div>

          {request.provider_legal?.legal_name && (
            <div className="mt-4 rounded-xl glass border border-border px-5 py-4 text-xs text-muted-foreground">
              <div className="uppercase tracking-widest font-mono mb-1.5">
                {t("routes.pay.providerLabel")}
              </div>
              <div className="text-foreground/90">{request.provider_legal.legal_name}</div>
              <div>
                {[
                  request.provider_legal.address,
                  request.provider_legal.postal_code,
                  request.provider_legal.city,
                ]
                  .filter(Boolean)
                  .join(", ")}
                {request.provider_legal.country ? ` · ${request.provider_legal.country}` : ""}
              </div>
              {request.provider_legal.vat_id && (
                <div>
                  {t("routes.pay.vat")}
                  {request.provider_legal.vat_id}
                </div>
              )}
            </div>
          )}

          {paid ? (
            <div className="mt-6 rounded-xl border border-[oklch(0.82_0.18_168_/_0.35)] bg-[oklch(0.82_0.18_168_/_0.06)] p-5 text-center">
              <Check className="h-7 w-7 mx-auto text-[oklch(0.82_0.18_168)]" />
              <div className="mt-2 font-display font-semibold">
                {t("routes.pay.paymentReceived")}
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                {t("routes.pay.notified", { name: request.provider_name })}
              </p>
              {!request.reviewed && !reviewDone ? (
                <div className="mt-5 pt-4 border-t border-border/60 text-left">
                  <div className="text-sm font-medium text-center">
                    {t("routes.pay.howWas", { name: request.provider_name })}
                  </div>
                  <div className="mt-2 flex justify-center gap-1">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button key={n} onClick={() => setStars(n)} aria-label={`${n} stars`}>
                        <Star
                          className={`h-7 w-7 transition ${n <= stars ? "text-[oklch(0.85_0.15_85)] fill-current" : "text-muted-foreground"}`}
                        />
                      </button>
                    ))}
                  </div>
                  {stars > 0 && (
                    <>
                      <textarea
                        value={reviewText}
                        onChange={(e) => setReviewText(e.target.value)}
                        maxLength={1000}
                        rows={2}
                        placeholder={t("routes.pay.reviewPh")}
                        className="mt-3 w-full rounded-xl bg-white/5 border border-border px-3 py-2 text-sm resize-none focus:outline-none focus:border-[oklch(0.82_0.18_168_/_0.6)]"
                      />
                      <button
                        onClick={sendReview}
                        disabled={reviewBusy}
                        className="mt-2 w-full rounded-full brand-gradient text-black px-4 py-2 text-sm font-medium disabled:opacity-50"
                      >
                        {reviewBusy ? t("routes.pay.submitting") : t("routes.pay.submitReview")}
                      </button>
                    </>
                  )}
                </div>
              ) : (
                (request.reviewed || reviewDone) && (
                  <p className="mt-4 text-xs text-muted-foreground flex items-center justify-center gap-1">
                    <Star className="h-3.5 w-3.5 text-[oklch(0.85_0.15_85)] fill-current" />{" "}
                    {t("routes.pay.reviewSubmitted")}
                  </p>
                )
              )}
            </div>
          ) : (
            <>
              <button
                onClick={pay}
                disabled={paying}
                className="mt-6 w-full rounded-full brand-gradient text-black px-5 py-3 text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {paying ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <CreditCard className="h-4 w-4" />
                )}
                {paying ? t("routes.pay.redirecting") : t("routes.pay.payBtn")}
              </button>
              <p className="mt-3 text-[11px] text-muted-foreground text-center flex items-center justify-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5" /> {t("routes.pay.stripeNote")}
              </p>
            </>
          )}
        </div>
      </section>
    </Layout>
  );
}
